import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environments';
import { ErrorHandler } from '../utils/error-handler';
import {
  GetAlternateCapacityDetailsRequest,
  GetAlternateCapacityDetailsResponse,
  GetAlternateCapacityFilterDropDownsRequest,
  GetAlternateCapacityFilterDropDownsResponse,
  GetAlternateCapacityCascadingFilterRequest,
  GetAlternateCapacityCascadingFilterResponse,
  RemoveAlternateCapacityDetailsRequest,
  RemoveAlternateCapacityDetailsResponse,
  SaveAlternateCapacityRequest,
  AlternateCapacityRow,
  DropdownOption,
  CascadingFilterOption
} from './interfaces/alternate-capcity.interface';

@Injectable({
  providedIn: 'root'
})
export class AlternateCapacityService {
  private baseUrl = environment.WebApiURL;
  private readonly alternateCapacityExportTabName = 'OilAndGasAlternateCapacities';

  private toDateInputFormat(value: any): string {
    if (!value) {
      return '';
    }

    const stringValue = String(value);
    if (stringValue.includes('T')) {
      return stringValue.split('T')[0];
    }

    return stringValue;
  }

  private getDetailsUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetAlternateCapacityDetails`;
  }

  private getFilterDropdownsUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetAlternateCapacityFilterDropDowns`;
  }

  private getCascadingFilterUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetAlternateCapacityCascadingFilter`;
  }

  private removeDetailsUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/RemoveAlternateCapacityDetails`;
  }

  private saveCapacityUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/InsertUpdateAlternateCapacity`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllAlternateCapacitiesData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.alternateCapacityExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawFileName = fileNameMatch?.[1]?.trim() || 'alternate-capacity.csv';
    const decodedFileName = decodeURIComponent(rawFileName.replace(/^"|"$/g, ''));
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName) ? decodedFileName : `${decodedFileName}.csv`;

    return { blob, fileName };
  }

  private parseDropdownResponse(response: GetAlternateCapacityFilterDropDownsResponse): {
    developmentAreas: string[];
    subDevelopmentAreas: string[];
    facilityNames: string[];
    alternateCapacityTypes: string[];
  } {
    const developmentAreaItem = response.find((item: DropdownOption) => item.columnName === 'DevelopmentArea');
    const subDevelopmentAreaItem = response.find((item: DropdownOption) => item.columnName === 'SubDevelopmentArea');
    const facilityNameItem = response.find((item: DropdownOption) => item.columnName === 'FacilityName');
    const alternateCapacityTypeItem = response.find((item: DropdownOption) => item.columnName === 'AlternateCapacityType');

    return {
      developmentAreas: developmentAreaItem?.values || [],
      subDevelopmentAreas: subDevelopmentAreaItem?.values || [],
      facilityNames: facilityNameItem?.values || [],
      alternateCapacityTypes: alternateCapacityTypeItem?.values || []
    };
  }

  async getAllDropdownOptions(): Promise<{
    developmentAreas: string[];
    subDevelopmentAreas: string[];
    facilityNames: string[];
    alternateCapacityTypes: string[];
  }> {
    try {
      const requestBody: GetAlternateCapacityFilterDropDownsRequest = {
        developmentArea: null,
        facilityName: null
      };

      const response = await firstValueFrom(
        this.http.post<GetAlternateCapacityFilterDropDownsResponse>(
          this.getFilterDropdownsUrl(),
          requestBody
        )
      );

      return this.parseDropdownResponse(response);
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getDevelopmentAreasByFacilityName(facilityName: string): Promise<{
    developmentAreas: string[];
    subDevelopmentAreas: string[];
  }> {
    try {
      const requestBody: GetAlternateCapacityFilterDropDownsRequest = {
        developmentArea: null,
        facilityName: facilityName
      };

      const response = await firstValueFrom(
        this.http.post<GetAlternateCapacityFilterDropDownsResponse>(
          this.getFilterDropdownsUrl(),
          requestBody
        )
      );

      const parsed = this.parseDropdownResponse(response);
      return {
        developmentAreas: parsed.developmentAreas,
        subDevelopmentAreas: parsed.subDevelopmentAreas
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.getDevelopmentAreasByFacilityName');
      return {
        developmentAreas: [],
        subDevelopmentAreas: []
      };
    }
  }

  async getFacilityNamesByDevelopmentArea(developmentArea: string): Promise<string[]> {
    try {
      const requestBody: GetAlternateCapacityFilterDropDownsRequest = {
        developmentArea: developmentArea,
        facilityName: null
      };

      const response = await firstValueFrom(
        this.http.post<GetAlternateCapacityFilterDropDownsResponse>(
          this.getFilterDropdownsUrl(),
          requestBody
        )
      );

      const facilityNameItem = response.find((item: DropdownOption) => item.columnName === 'FacilityName');
      return facilityNameItem?.values || [];
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.getFacilityNamesByDevelopmentArea');
      return [];
    }
  }

  async getCascadingFilterOptions(filters?: Partial<GetAlternateCapacityCascadingFilterRequest>): Promise<GetAlternateCapacityCascadingFilterResponse> {
    try {
      const requestBody: GetAlternateCapacityCascadingFilterRequest = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        alternateCapacityType: filters?.alternateCapacityType || null
      };

      const response = await firstValueFrom(
        this.http.post<GetAlternateCapacityCascadingFilterResponse>(
          this.getCascadingFilterUrl(),
          requestBody
        )
      );

      return response || [];
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.getCascadingFilterOptions');
      return [];
    }
  }

  async getCapacitiesWithPagination(filters?: Partial<GetAlternateCapacityDetailsRequest>): Promise<{
    capacities: AlternateCapacityRow[];
    nextRowNumber: number | null;
  }> {
    try {
      const payload: GetAlternateCapacityDetailsRequest = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        alternateCapacityType: filters?.alternateCapacityType || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.getDetailsUrl(), payload)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        const rawItems = response.capacityItems || response.gasItem || [];
        const capacities: AlternateCapacityRow[] = rawItems.map((item: any) => ({
          devArea: item.devArea ?? item.developmentArea ?? '',
          alternateCapacityPlant: item.alternateCapacityPlant ?? item.facilityName ?? '',
          alternateCapacityType: item.alternateCapacityType ?? '',
          effectiveDate: this.toDateInputFormat(item.effectiveDate),
          alternateLimitQuantity: item.alternateLimitQuantity ?? item.capacity ?? null,
          isNew: false,
          isEdited: false
        }));

        return {
          capacities,
          nextRowNumber: response.nextRowNumber ?? null
        };
      }

      return {
        capacities: [],
        nextRowNumber: null
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.getCapacitiesWithPagination');
      return {
        capacities: [],
        nextRowNumber: null
      };
    }
  }

  async removeCapacity(row: AlternateCapacityRow): Promise<any> {
    try {
      const body = {
        developmentArea: row.devArea || '',
        facilityName: row.alternateCapacityPlant || '',
        alternateCapacityType: row.alternateCapacityType || '',
        effectiveDate: row.effectiveDate || ''
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.removeDetailsUrl(), body)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveCapacity(row: AlternateCapacityRow, action: 'INSERT' | 'UPDATE', userName: string): Promise<any> {
    try {
      const body = {
        action: action,
        developmentArea: row.devArea || '',
        facilityName: row.alternateCapacityPlant || '',
        alternateCapacityType: row.alternateCapacityType || '',
        capacity: row.alternateLimitQuantity ?? 0,
        effectiveDate: row.effectiveDate || '',
        oldEffectiveDate: action === 'UPDATE' && row.original?.effectiveDate ? row.original.effectiveDate : '',
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.saveCapacityUrl(), body)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveMultipleCapacities(capacities: AlternateCapacityRow[], userName: string): Promise<any[]> {
    try {
      const results: any[] = [];
      
      for (const capacity of capacities) {
        const action = capacity.isNew ? 'INSERT' : 'UPDATE';
        
        const result = await this.saveCapacity(capacity, action, userName);
        results.push(result);
      }

      return results;
    } catch (error: any) {
      ErrorHandler.handleError(error, 'AlternateCapacityService.saveMultipleCapacities');

      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  mapApiResponseToRows(response: GetAlternateCapacityDetailsResponse): AlternateCapacityRow[] {
    if (!response.capacityItems || response.capacityItems.length === 0) {
      return [];
    }

    return response.capacityItems.map((item: any) => ({
      devArea: item.developmentArea,
      alternateCapacityPlant: item.facilityName,
      alternateCapacityType: item.alternateCapacityType,
      effectiveDate: item.effectiveDate,
      alternateLimitQuantity: item.capacity,
      isNew: false,
      isEdited: false
    }));
  }
}
